/**
 * Composable para tour de navegação do dashboard
 * Gerencia todo o fluxo de onboarding e navegação
 */

import { useI18n } from 'vue-i18n'
import { ref, computed, onMounted, nextTick, readonly } from 'vue'
import { useRoute } from 'vue-router'
import { useMediaQuery } from '@vueuse/core'
import { MediaQuery } from '@cplug/design-system/components/props'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { useIntroJs, type TourStep, type TourCallbacks } from '~/shared/composables/useIntroJs'
import useMenuSidebarStore from '~/shared/stores/useMenuSidebarStore'

export interface NavigationTourOptions {
  /** Iniciar automaticamente */
  autoStart?: boolean
  /** Delay para início automático (ms) */
  autoStartDelay?: number
  /** Seletores customizados */
  customSelectors?: {
    navigation?: string
    products?: string
    stock?: string
    customers?: string
    finance?: string
    pos?: string
    settings?: string
  }
}

export function useNavigationTour (options: NavigationTourOptions = {}): any {
  const {
    autoStart = true,
    autoStartDelay = 100,
    customSelectors = {}
  } = options

  // Composables
  const { startTour } = useIntroJs()
  const { t } = useI18n()
  const route = useRoute()
  const menuSidebarStore = useMenuSidebarStore()
  const isDesktop = useMediaQuery(MediaQuery.Desktop)

  // Estado do tour
  const currentStepIndex = ref<number>(0)
  const isActive = ref<boolean>(false)

  // Seletores padrão com override pelos options
  const selectors = computed(() => ({
    navigation: isDesktop.value ? '.menu-sidebar' : '.menu-sidebar-mobile',
    products: '[data-tour="menu-products"]',
    stock: '[data-tour="menu-stock"]',
    customers: '[data-tour="menu-customers"]',
    finance: '[data-tour="menu-finance"]',
    pos: '[data-tour="menu-pos"]',
    settings: '[data-tour="menu-settings"]',
    ...customSelectors
  }))

  /**
   * Configuração dos passos do tour com i18n
   */
  const tourSteps = computed((): TourStep[] => [
    {
      title: t('NavigationTourWelcomeMessage'),
      intro: t('NavigationTourWelcomeMessageDescription')
    },
    {
      element: selectors.value.navigation,
      title: t('NavigationTourNavigationArea'),
      position: 'right',
      intro: t('NavigationTourNavigationAreaDescription')
    },
    {
      element: selectors.value.products,
      title: t('NavigationTourProductsArea'),
      position: 'right',
      intro: t('NavigationTourProductsAreaDescription')
    },
    {
      element: selectors.value.stock,
      title: t('NavigationTourStockArea'),
      position: 'right',
      intro: t('NavigationTourStockAreaDescription')
    },
    {
      element: selectors.value.customers,
      title: t('NavigationTourCustomersArea'),
      position: 'right',
      intro: t('NavigationTourCustomersAreaDescription')
    },
    {
      element: selectors.value.finance,
      title: t('NavigationTourFinanceArea'),
      position: 'right',
      intro: t('NavigationTourFinanceAreaDescription')
    },
    {
      element: selectors.value.pos,
      title: t('NavigationTourPosArea'),
      position: 'right',
      intro: t('NavigationTourPosAreaDescription')
    },
    {
      element: selectors.value.settings,
      title: t('NavigationTourSettingsArea'),
      position: 'right',
      intro: t('NavigationTourSettingsAreaDescription')
    },
    {
      title: t('NavigationTourReadyToStart'),
      intro: t('NavigationTourReadyToStartMessage')
    }
  ])

  /**
   * Callbacks do tour para controle de menu mobile/tablet/desktop
   */
  const tourCallbacks: TourCallbacks = {
    onStart: () => {
      currentStepIndex.value = 0
      isActive.value = true
    },

    onExit: () => {
      isActive.value = false
      // Fechar menu se saiu do tour
      if (!isDesktop.value) {
        menuSidebarStore.close()
      } else {
        menuSidebarStore.collapsePrimarySidebar()
      }
    },

    onComplete: () => {
      isActive.value = false
      // Fechar menu quando completar
      setTimeout(() => {
        if (!isDesktop.value) {
          menuSidebarStore.close()

          menuSidebarStore.collapsePrimarySidebar()
        }
      }, 1000)

      // Marcar tour como concluído
      localStorage.setItem('navigation_tour_completed', 'true')
    }
  }

  const isStoreOnboardingRoute = (): boolean => route.path.includes('/dashboard/store/onboarding')

  /**
   * Inicia o tour de navegação
   */
  const startNavigationTour = async (): Promise<void> => {
    if (isStoreOnboardingRoute()) {
      return
    }

    try {
      // No mobile, abrir o drawer primeiro e aguardar antes de iniciar o tour
      if (!isDesktop.value) {
        menuSidebarStore.open()
        // Aguardar a animação do drawer terminar
        await new Promise(resolve => setTimeout(resolve, 400))
      } else {
        menuSidebarStore.expandPrimarySidebar()
      }

      await startTour(tourSteps.value, {
        showProgress: true,
        nextLabel: t('NavigationTourNextLabel'),
        prevLabel: t('NavigationTourPrevLabel'),
        doneLabel: t('NavigationTourDoneLabel'),
        skipLabel: t('NavigationTourSkipLabel'),
        showBullets: false,
        tooltipClass: 'navigation-tour-tooltip',
        highlightClass: 'navigation-tour-highlight',
        callbacks: tourCallbacks
      })
    } catch (error) {
      // Erro ao iniciar tour - falha silenciosa
      isActive.value = false
    }
  }

  /**
   * Para o tour atual
   */
  const stopNavigationTour = (): void => {
    const { exitTour } = useIntroJs()
    exitTour()
    isActive.value = false
  }

  /**
   * Verifica se deve iniciar automaticamente
   */
  const checkAutoStart = (): void => {
    if (isStoreOnboardingRoute()) {
      return
    }

    const authStore = useAuthenticationStore()
    const hasCompleted = localStorage.getItem('navigation_tour_completed')

    if (autoStart && !hasCompleted && !isActive.value && authStore.logged) {
      setTimeout(() => {
        startNavigationTour()
      }, autoStartDelay)
    }
  }

  /**
   * Reseta o tour (remove flag de concluído)
   */
  const resetNavigationTour = (): void => {
    localStorage.removeItem('navigation_tour_completed')
  }

  /**
   * Verifica se o tour foi concluído
   */
  const hasCompletedTour = (): boolean => {
    return localStorage.getItem('navigation_tour_completed') === 'true'
  }

  // Auto-start se configurado
  onMounted(() => {
    nextTick(() => {
      checkAutoStart()
    })
  })

  return {
    // Estado
    currentStepIndex: readonly(currentStepIndex),
    isActive: readonly(isActive),

    // Configurações
    selectors: readonly(selectors),
    tourSteps: readonly(tourSteps),

    // Métodos
    startNavigationTour,
    stopNavigationTour,
    resetNavigationTour,
    hasCompletedTour,

    // Utils
    checkAutoStart
  }
}
